/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { type RefObject } from 'react';

import { css, jsx, keyframes } from '@compiled/react';
import { useIntl } from 'react-intl';

import IconButton from '@atlaskit/button/icon/button';
import AiSearchIcon from '@atlaskit/icon-lab/core/ai-search';
import CrossIcon from '@atlaskit/icon/core/cross';
import ChatIcon from '@atlaskit/icon/core/rovo-chat';
import SearchIcon from '@atlaskit/icon/core/search';
import { fg } from '@atlaskit/platform-feature-flags/fg';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box } from '@atlaskit/primitives/box';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss } from '@atlaskit/primitives/xcss/xcss';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';
import ColorfulSpinner from '@atlassian/conversation-assistant-ui-components/ui/ColorfulSpinner';

import { FiltersToggle } from '../../../../ui/quick-find/filter-toggle/async';
import type { ThirdPartyConfigsBootstrap } from '../../../constants/schemas/3p-config';
import { ChatIntentMode } from '../../../utils/quick-find/chat-intent-mode';
import { ConnectAppsSearchIcon } from './connect-apps-icon';
import { LegacySearchIcon } from './legacy-search-icon';
import { messages } from './messages';

const INTERACTIVE_SELECTOR = 'input, button, a[href], [role="button"]';

const textFieldBoxBaseStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	height: 'size.300',
	':focus': {
		height: 'size.400',
	},
	width: '100%',
	paddingRight: 'space.200',
});

const textFieldBoxStyles = xcss({
	borderStyle: 'solid',
	borderWidth: 'border.width',
	borderColor: 'color.border',
	backgroundColor: 'elevation.surface.overlay',
	borderRadius: 'radius.medium',
});

const textFieldBoxFocusedStyles = xcss({
	height: 'size.400',
	border: 'none',
	paddingRight: '0',
});

const textFieldBoxFocusedElevationStyles = xcss({
	boxShadow: 'elevation.shadow.overlay',
});

const textFieldInDialogStyles = xcss({
	marginTop: 'space.075',
	position: 'relative',
	zIndex: 'layer',
});

const inputStyles = css({
	outline: 'none',
	background: 'none',
	font: token('font.body'),
	color: token('color.text.subtlest'),
	border: 'none',
	width: '100%',
	padding: '0',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',

	'&::placeholder': {
		color: token('color.text.subtlest'),
	},
});

const inputFocusedStyles = css({
	color: token('color.text'),
});

const iconWrapperStyles = xcss({
	minWidth: 'size.300',
	minHeight: 'size.300',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	color: 'color.text.subtlest',
});

const iconWrapperFocusedStyles = xcss({
	marginInlineStart: 'space.200',
	marginInlineEnd: 'space.150',
	minWidth: 'size.200',
});

const clearIconFadeInAnimation = keyframes({
	from: {
		opacity: 0,
	},
	to: {
		opacity: 1,
	},
});

const clearIconFadeInWrapperStyles = css({
	marginRight: token('space.100', '8px'),
	animation: `${clearIconFadeInAnimation} 150ms ease-out`,
	animationFillMode: 'forwards',
});

// Styles for the trailing Connect Apps icon slot.
const connectAppsIconStyles = xcss({
	marginRight: 'space.050',
	display: 'flex',
	alignItems: 'center',
});

// When QuickFind is open, align the pill with where the close icon sits.
const connectAppsIconOpenStyles = xcss({
	marginRight: 'space.100',
});

const connectAppsIconWithFilterToggleStyles = xcss({
	marginInlineStart: 'space.100',
});

const textFieldBoxNoRightPaddingStyles = xcss({
	paddingRight: 'space.0',
});

// Nav3 style overrides
// Can be removed once all supported products use Nav4
const nav3StyleOverrides = {
	textFieldBoxStyles: xcss({
		borderColor: 'color.border.input',
		backgroundColor: 'color.background.input',
		borderRadius: 'radius.medium',
		width: '200px',
	}),
	textFieldBoxFocusedStyles: xcss({
		backgroundColor: 'elevation.surface.overlay',
		borderRadius: 'radius.large',
		width: '780px',
	}),
	iconWrapperStyles: xcss({
		minWidth: '30px',
	}),
	inputStyles: css({
		font: token('font.body'),
	}),
};

export type QuickFindFilterPresentation = {
	regionId: string;
	isExpanded: boolean;
	onToggle: () => void;
};

export type TextfieldProps = {
	value?: string;
	dialogId: string;
	quickFindOpen: boolean;
	isNav4Enabled?: boolean;
	isAdminHubAIEnabled?: boolean;
	intentRouterEnabled?: boolean;
	chatIntentDetectionEnabled?: boolean;
	isLoading?: boolean;
	chatIntentMode?: ChatIntentMode;
	/**
	 * Unified input+dialog surface (rovo_qf_instant_search treatment).
	 * Passed in by the caller that owns the experiment decision so this shared
	 * component (also rendered by the pre-hydration skeleton) never reads the
	 * experiment itself.
	 */
	unifiedSurface?: boolean;
	activeDescendantId?: string;
	onEnter?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
	onFocus?: React.FocusEventHandler<HTMLInputElement>;
	onBlur?: React.FocusEventHandler<HTMLInputElement>;
	onSelect?: React.ReactEventHandler<HTMLInputElement>;
	onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
	onChange?: React.ChangeEventHandler<HTMLInputElement>;
	onClick?: React.MouseEventHandler<HTMLInputElement>;
	onClear?: () => void;
	/**
	 * Called when the user clicks the connect-apps pill button. Forwarded to
	 * `<ConnectAppsSearchIcon>`'s built-in `onAppsClick` prop.
	 */
	onAppsClick?: () => void;
	inputRef?: RefObject<HTMLInputElement>;
	clearIconRef?: RefObject<HTMLButtonElement>;
	placeholder?: string;
	cloudId?: string;
	accountId?: string;
	orgId?: string;
	primaryProduct?: string;
	smartLinkUsage?: Record<string, number>;
	thirdPartyConfigs?: ThirdPartyConfigsBootstrap;
	filterPresentation?: QuickFindFilterPresentation;
};

const TextfieldIcon = ({
	quickFindOpen,
	isNav4Enabled,
	chatIntentDetectionEnabled,
	intentRouterEnabled,
	chatIntentMode,
	isLoading,
}: Pick<
	TextfieldProps,
	| 'quickFindOpen'
	| 'isNav4Enabled'
	| 'chatIntentDetectionEnabled'
	| 'intentRouterEnabled'
	| 'chatIntentMode'
	| 'isLoading'
>) => {
	if (chatIntentDetectionEnabled && (isNav4Enabled || quickFindOpen)) {
		if (quickFindOpen && !intentRouterEnabled && isLoading) {
			return <ColorfulSpinner size="small" testId="quick-find-loading-spinner" />;
		}

		if (quickFindOpen && !intentRouterEnabled && chatIntentMode === ChatIntentMode.CHAT) {
			return <ChatIcon color="currentColor" label="" testId="quick-find-chat-icon" />;
		}

		return <AiSearchIcon color="currentColor" label="" />;
	}

	if (isNav4Enabled || quickFindOpen) {
		return <SearchIcon color="currentColor" label="" aria-hidden="true" />;
	}

	return <LegacySearchIcon isExpanded={!!quickFindOpen} />;
};

export const Textfield: ({
	value,
	dialogId,
	quickFindOpen,
	isNav4Enabled,
	onEnter,
	onFocus,
	onBlur,
	onSelect,
	onKeyDown,
	onChange,
	onClick,
	onClear,
	onAppsClick,
	inputRef,
	clearIconRef,
	placeholder,
	intentRouterEnabled,
	chatIntentDetectionEnabled,
	chatIntentMode,
	isLoading,
	unifiedSurface,
	cloudId,
	accountId,
	primaryProduct,
}: TextfieldProps) => JSX.Element = ({
	value,
	dialogId,
	quickFindOpen,
	isNav4Enabled,
	onEnter,
	onFocus,
	onBlur,
	onSelect,
	onKeyDown,
	onChange,
	onClick,
	onClear,
	onAppsClick,
	inputRef,
	clearIconRef,
	placeholder,
	intentRouterEnabled,
	chatIntentDetectionEnabled,
	chatIntentMode,
	isLoading,
	unifiedSurface,
	activeDescendantId,
	cloudId,
	accountId,
	orgId,
	primaryProduct,
	smartLinkUsage,
	thirdPartyConfigs,
	filterPresentation,
}: TextfieldProps) => {
	const intl = useIntl();
	const showFilterToggle = quickFindOpen && filterPresentation !== undefined;
	const controlSpacing = chatIntentDetectionEnabled && quickFindOpen ? 'default' : 'compact';

	const instructionsId = `${dialogId}-instructions`;

	// Input drops its own border/shadow/radius and overlays onto the dialog (which
	// owns the single shadow). Driven by the caller, not read here — see prop doc.
	const unified = intentRouterEnabled || unifiedSurface || chatIntentDetectionEnabled;

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (!e.defaultPrevented && e.key === 'Enter') {
			e.preventDefault();
			onEnter && onEnter(e);
		}
		if (typeof onKeyDown === 'function') {
			onKeyDown(e);
		}
	};

	const clearIconButton = (
		<IconButton
			ref={clearIconRef}
			icon={(iconProps) => <CrossIcon {...iconProps} color={token('color.icon')} label="" />}
			spacing={controlSpacing}
			onClick={onClear}
			label={intl.formatMessage(messages.clearIconLabel)}
			appearance="subtle"
			testId="search-input-clear-button"
		/>
	);

	return (
		// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
		<Box
			xcss={[
				textFieldBoxBaseStyles,
				(!unified || !quickFindOpen) && textFieldBoxStyles,
				unified && quickFindOpen && textFieldInDialogStyles,
				quickFindOpen ? textFieldBoxFocusedStyles : false,
				!unified && quickFindOpen && textFieldBoxFocusedElevationStyles,
				!isNav4Enabled && nav3StyleOverrides.textFieldBoxStyles,
				!isNav4Enabled && quickFindOpen && nav3StyleOverrides.textFieldBoxFocusedStyles,
				textFieldBoxNoRightPaddingStyles,
			]}
			onClick={(e: React.MouseEvent<HTMLDivElement>) => {
				// React's synthetic events bubble through the JSX tree, not the DOM. The Rovo
				// Settings modal is a portal, so clicks inside it bubble to this onClick even
				// though they're outside this DOM subtree. Guard against that here, otherwise
				// closing the modal re-focuses the input and reopens Quick Find.
				if (!e.currentTarget.contains(e.target as HTMLElement)) {
					return;
				}
				if (fg('rovo_search_chat_convergence_instrumentation')) {
					const interactiveTarget = (e.target as HTMLElement).closest(INTERACTIVE_SELECTOR);
					if (interactiveTarget && e.currentTarget.contains(interactiveTarget)) {
						return;
					}
				}
				inputRef?.current?.click();
				inputRef?.current?.focus();
			}}
			testId="search-input-container"
			role="search"
		>
			<Box
				xcss={[
					iconWrapperStyles,
					quickFindOpen && iconWrapperFocusedStyles,
					!isNav4Enabled && nav3StyleOverrides.iconWrapperStyles,
				]}
			>
				<TextfieldIcon
					quickFindOpen={quickFindOpen}
					isNav4Enabled={isNav4Enabled}
					chatIntentDetectionEnabled={chatIntentDetectionEnabled}
					intentRouterEnabled={intentRouterEnabled}
					chatIntentMode={chatIntentMode}
					isLoading={isLoading}
				/>
			</Box>
			<input
				ref={inputRef}
				css={[
					inputStyles,
					quickFindOpen && inputFocusedStyles,
					!isNav4Enabled && nav3StyleOverrides.inputStyles,
				]}
				onFocus={onFocus}
				onBlur={onBlur}
				onSelect={onSelect}
				onKeyDown={handleKeyDown}
				onClick={onClick}
				placeholder={placeholder || intl.formatMessage(messages.inputPlaceholder)}
				onChange={onChange}
				value={value}
				data-testid="search-input"
				autoComplete="off"
				aria-label={intl.formatMessage(messages.inputPlaceholder)}
				aria-describedby={instructionsId}
				aria-expanded={quickFindOpen}
				aria-controls={dialogId}
				aria-activedescendant={activeDescendantId ?? ''}
				aria-haspopup="true"
				role="combobox"
			/>
			<VisuallyHidden id={instructionsId}>
				{intl.formatMessage(messages.inputInstructions)}
			</VisuallyHidden>
			{quickFindOpen && value && <div css={clearIconFadeInWrapperStyles}>{clearIconButton}</div>}
			{showFilterToggle && <FiltersToggle {...filterPresentation} />}
			<Box
				xcss={[
					connectAppsIconStyles,
					quickFindOpen && connectAppsIconOpenStyles,
					showFilterToggle && connectAppsIconWithFilterToggleStyles,
				]}
			>
				<ConnectAppsSearchIcon
					spacing={controlSpacing}
					start={quickFindOpen && !!thirdPartyConfigs}
					cloudId={cloudId}
					accountId={accountId}
					orgId={orgId}
					primaryProduct={primaryProduct}
					smartLinkUsage={smartLinkUsage}
					thirdPartyConfigs={thirdPartyConfigs}
					onAppsClick={onAppsClick}
				/>
			</Box>
		</Box>
	);
};
